import { Injectable } from '@angular/core';
import { UserManager, UserManagerSettings, User } from 'oidc-client';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { ClientConfigService } from '../clientconfig/clientconfig.service'
import { Observable, from, of, ReplaySubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private manager: UserManager = null;
  private user: User = null;
  private route: ActivatedRouteSnapshot = null;
  private router: Router = null;
  private userLoaded$ = new ReplaySubject<User>(1);

  constructor(router: Router, private http: HttpClient) {
    this.router = router;

    this.getManager();
  }

  isLoggedIn(): boolean {
    return this.user !== null && !this.user.expired;
  }

  isLoggedIn$(): Observable<boolean> {
    return this.userLoaded$.pipe(
      take(1),
      map(() => this.user !== null && !this.user.expired)
    );
  }

  logout() {
    this.manager.signoutRedirect();
  }

  getClaims(): any {
    return this.user.profile;
  }

  hasClaim(claim: string): boolean {
    return ((this.user !== null) && (this.user.profile != null) && (claim in this.user.profile));
  }

  getAuthorizationHeaderValue(): string {
    if (!this.user) {
      console.warn('AuthService.getAuthorizationHeaderValue called but user is null');
      return '';
    }
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  getManager() {
    if (this.manager !== null) return this.manager;

    this.manager = new UserManager(getClientSettings(ClientConfigService.settings));
    this.manager.events.addAccessTokenExpired(_ => {
      this.manager.signinSilent().then(user => {
        this.user = user;
      });
    });

    this.manager.getUser().then(user => {
      this.user = user;
      this.userLoaded$.next(user);
    });

    return this.manager;
  }

  getResolvedUrl(route: ActivatedRouteSnapshot): string {
  return route.pathFromRoot
    .map(v => v.url.map(segment => segment.toString()).join('/'))
    .join('/');
  }

  startAuthentication(route: RouterStateSnapshot) {
    const url = route.url.toString();

    const mgr = this.getManager();

    mgr.signinRedirect({ state: url });
  }

  completeAuthentication() {
    const mgr = this.getManager();

    return mgr.signinRedirectCallback().then(user => {
      this.user = user;

      const url = this.user.state;

      // Don't attempt navigation here - let the auth-callback component handle it
      // This prevents race conditions with profile loading
      return user;
    });
  }


  completeSilentSignin() {
    const mgr = this.getManager();

    return mgr.signinSilentCallback();
  }
  startLogOut() {
    if (this.isLoggedIn()) {
      const mgr = this.getManager();

      return mgr.signoutRedirect();
    }
  }

  completeLogOut() {
    const mgr = this.getManager();

    return mgr.signoutRedirectCallback().then(user => {
      this.user = null;
    });
  }

}

export function getClientSettings(cfg): UserManagerSettings {
  return {
    authority: cfg.authority,
    client_id: cfg.client_id,
    redirect_uri: cfg.redirect_uri,
    post_logout_redirect_uri: cfg.post_logout_redirect_uri,
    response_type: cfg.response_type,
    scope: cfg.scope,
    automaticSilentRenew: cfg.automaticSilentRenew,
    silent_redirect_uri: cfg.silent_redirect_uri
  };
}
